import React, { useMemo } from "react";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

import { Download, FileSpreadsheet, FileText } from "lucide-react";

import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

/* ------------------ utils ------------------ */
function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/* ------------------ Button ------------------ */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-sm-primary text-white hover:bg-sm-primary/90",
        destructive: "bg-red-600 text-white hover:bg-red-700",
        outline:
          "border border-gray-300 bg-white hover:bg-gray-100 text-gray-800",
        ghost: "hover:bg-gray-100",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 px-3",
        icon: "h-10 w-10 p-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

const Button = React.forwardRef(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

/* ---------- COLORS ---------- */
const COLORS = [
  "#3B0069",
  "#711AA7",
  "#FF7C01",
  "#168C1A",
  "#58267F",
  "#C21313",
];

const CHART_TEXT = "#737373";

/* ---------- COMPONENT ---------- */
export function SnagReporting({ snags = [] }) {
  /* ---------- Project Data ---------- */
  const projectData = useMemo(() => {
    const projects = Array.from(
      new Set(snags.map((s) => s.project).filter(Boolean)),
    );
    return projects.map((p) => ({
      name: p.split(" ")[0],
      count: snags.filter((s) => s.project === p).length,
    }));
  }, [snags]);

  /* ---------- Category Data ---------- */
  const categoryData = useMemo(() => {
    const categories = Array.from(
      new Set(snags.map((s) => s.category).filter(Boolean)),
    );
    return categories.map((c) => ({
      name: c,
      value: snags.filter((s) => s.category === c).length,
    }));
  }, [snags]);

  /* ---------- Assignee Data (SAFE FIX) ---------- */
  const assigneeData = useMemo(() => {
    const assignees = Array.from(
      new Set(snags.flatMap((s) => s.assignedTo || [])),
    ).filter(Boolean);
    
    return assignees.map((a) => ({
      name: a.split(" ")[0],
      open: snags.filter(
        (s) =>
          (s.assignedTo || []).includes(a) &&
          ["Open", "Pending Approval", "Issue Raised"].includes(s.status),
      ).length,
      closed: snags.filter(
        (s) =>
          (s.assignedTo || []).includes(a) &&
          ["Resolved", "Closed"].includes(s.status),
      ).length,
    }));
  }, [snags]);

  /* ---------- Trend Data ---------- */
  const trendData = useMemo(() => {
    const months = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun", 
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
    ];
    const monthCounts = {};

    snags.forEach((snag) => {
      if (!snag.createdAt) return;
      
      const date = new Date(snag.createdAt);
      if (isNaN(date.getTime())) return;

      const year = date.getFullYear();
      const monthIndex = date.getMonth();
      const monthKey = `${year}-${String(monthIndex + 1).padStart(2, "0")}`;
      const monthLabel = months[monthIndex];

      if (!monthCounts[monthKey]) {
        monthCounts[monthKey] = {
          monthKey,
          month: monthLabel,
          created: 0,
          closed: 0,
        };
      }

      monthCounts[monthKey].created += 1;
      if (["Resolved", "Closed"].includes(snag.status)) {
        monthCounts[monthKey].closed += 1;
      }
    });

    return Object.values(monthCounts)
      .sort((a, b) => a.monthKey.localeCompare(b.monthKey))
      .map(({ month, created, closed }) => ({ month, created, closed }));
  }, [snags]);

  return (
    <div className="space-y-5">
      {/* HEADER */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-lg font-semibold">Reports & Analytics</h3>

        <div className="flex gap-2">
          {/* <Button size="sm" variant="outline">
            <FileSpreadsheet className="mr-1 h-4 w-4" />
            Excel
          </Button>

          <Button size="sm" variant="outline">
            <FileText className="mr-1 h-4 w-4" />
            PDF
          </Button> */}

          <Button size="sm" variant="outline">
            <Download className="mr-1 h-4 w-4" />
            Export
          </Button>
        </div>
      </div>

      {/* CHARTS */}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        {/* Project Chart */}
        <div className="rounded-xl border p-5">
          <h4 className="mb-3 text-sm font-semibold uppercase">
            Project-wise Snags
          </h4>

          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={projectData}>
              <XAxis dataKey="name" tick={{ fill: CHART_TEXT }} />
              <YAxis tick={{ fill: CHART_TEXT }} />
              <Tooltip />
              <Bar dataKey="count" fill="#3B0069" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Category Pie */}
        <div className="rounded-xl border p-5">
          <h4 className="mb-3 text-sm font-semibold uppercase">
            Category Distribution
          </h4>

          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={categoryData} dataKey="value" outerRadius={80} label>
                {categoryData.map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Assignee Chart */}
        <div className="rounded-xl border p-5">
          <h4 className="mb-3 text-sm font-semibold uppercase">
            Assignee Performance
          </h4>

          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={assigneeData}>
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="open" fill="#FF7C01" />
              <Bar dataKey="closed" fill="#168C1A" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Trend Chart */}
        <div className="rounded-xl border p-5">
          <h4 className="mb-3 text-sm font-semibold uppercase">
            Closure Trend
          </h4>

          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={trendData}>
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="created" stroke="#3B0069" />
              <Line type="monotone" dataKey="closed" stroke="#168C1A" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
